import { Helmet } from "react-helmet";
import styled from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { Row, Form, Spin, Empty, Space, Table, Typography, ConfigProvider } from "antd";
import { StringParam, NumberParam, withDefault, useQueryParams } from "use-query-params";

import type { ColumnsType } from "antd/es/table";

import Role from "components/Role";
import EditableCell from "components/EditableCell";
import { ApiErrorTableComponent } from "components/ErrorHandler";

import { useAppDispatch } from "app/hooks";
import { openNotificationWithIcon } from "utils";
import { updateLayout } from "pages/Generation/generationSlice";
import { useUpdateBatchTransactionMutation } from "pages/Generation/generationApiSlice";
import { useGetTransactionsWithErrorQuery } from "pages/TPlusOneProcessing/tPlusOneProcessingApiSlice";
import { TransactionDTO, TransactionType, TransactionUpdateDTO } from "models";

const GenerationTableContainer = styled.div`
  .editable-row .ant-form-item-explain {
    position: absolute;
    top: 100%;
    font-size: 12px;
  }

  .ant-table-content {
    border-radius: 4px;
  }

  colgroup {
    border-radius: 4px;
    col {
      border-radius: 4px;
    }
  }
`;

const GenerationEditableTable: React.FC = () => {
  const [form] = Form.useForm<TransactionDTO>();
  const navigate = useNavigate();
  const params = useParams<{ batchId: string }>();
  const dispatch = useAppDispatch();

  const [transactionParams, setTransactionParams] = useQueryParams({
    page: withDefault(NumberParam, 0),
    size: withDefault(NumberParam, 10),
    batchId: withDefault(StringParam, params.batchId),
  });

  const [updateSingleTransaction, updateSingleTransactionResult] =
    useUpdateBatchTransactionMutation();
  const transactionsWithError = useGetTransactionsWithErrorQuery(transactionParams);

  const [editingID, setEditingID] = useState(-1);
  const [formData, setFormData] = useState<TransactionDTO[]>(
    transactionsWithError.data?.response.content || [],
  );

  const isEditing = (record: TransactionDTO) => {
    return record.id === editingID;
  };

  const edit = (record: Partial<TransactionDTO>) => {
    form.setFieldsValue({
      id: record.id,
      payor: record.payor,
      batchID: record.batchID,
      sortCode: record.sortCode,
      merchant: record.merchant,
      narration: record.narration,
      serialNumber: record.serialNumber,
      accountNumber: record.accountName,
      ...record,
    });

    setEditingID(record.id as number);
  };

  const cancel = () => {
    setEditingID(-1);
  };

  const save = async (key: React.Key) => {
    try {
      const row = (await form.validateFields()) as TransactionDTO;

      const newData = [...formData];
      const index = newData.findIndex((item) => key === item.id);
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          ...row,
        });

        setEditingID(-1);
        setFormData(newData);
        handleUpdateBatchTransaction({ ...item, ...row });
      } else {
        newData.push(row);
        setFormData(newData);

        setEditingID(-1);
      }
    } catch (errInfo) {}
  };

  const handleTableChange = (
    pagination: { current?: number; pageSize?: number },
    _filters: unknown,
    _sorter: unknown,
  ) => {
    setTransactionParams({
      page: pagination.current - 1,
      size: pagination.pageSize,
    });
  };

  const handleUpdateBatchTransaction = ({
    id,
    payor,
    batchID,
    sortCode,
    merchant,
    narration,
    accountName,
    serialNumber,
    accountNumber,
  }: TransactionDTO) => {
    const updateTransactionObj: TransactionUpdateDTO = {
      id,
      payor,
      batchID,
      merchant,
      sortCode,
      narration,
      accountName,
      serialNumber,
      accountNumber,
    };

    updateSingleTransaction(updateTransactionObj)
      .unwrap()
      .then((payload) => {
        openNotificationWithIcon(``, payload.response, "success");
      })
      .catch((error) => {
        if (error.status >= 500 && error.status < 600) {
          openNotificationWithIcon(error.status, error.data, "error");
        } else if (error.status === 400) {
          error.data?.fieldErrorList?.length
            ? error.data?.fieldErrorList?.forEach((errorMessageObj: any) =>
                openNotificationWithIcon("unsuccessful", errorMessageObj.errorMessage, "warning"),
              )
            : openNotificationWithIcon(error.data.errorMessage, "", "warning");
        }
      });
  };

  useEffect(() => {
    dispatch(
      updateLayout({
        title: `Generation - Edit Error Transactions - ${params.batchId}`,
        function: "EDIT",
        path: "",
      }),
    );
  }, [dispatch, params.batchId]);

  useEffect(() => {
    if (
      transactionsWithError.currentData?.response.content &&
      !transactionsWithError.currentData?.response.content.length
    ) {
      // navigate(-1);
    }
  }, [transactionsWithError.isFetching, transactionsWithError.currentData?.response.content]);

  useEffect(() => {
    if (transactionsWithError.data?.response.content) {
      setFormData(transactionsWithError.data?.response.content);
    }
  }, [transactionsWithError.data?.response.content]);

  useEffect(() => {
    if (transactionsWithError.isSuccess && !transactionsWithError.data?.response?.content?.length) {
      navigate(-1);
    }
  }, [transactionsWithError.isSuccess, transactionsWithError.data?.response?.content?.length, navigate]);

  useEffect(() => {
    if (transactionsWithError.isError) {
      const error = transactionsWithError?.error as { status: number; data: { error?: string; message?: string; errorMessage?: string; errorMessageList?: string[] } };
      if (error.status >= 500 && error.status < 600) {
        openNotificationWithIcon(error.data.error, error.data.message, "error");
      } else if (error.status === 400) {
        error.data?.errorMessageList?.length
          ? error.data?.errorMessageList?.forEach((errorMessage: string) =>
              openNotificationWithIcon(error.data.errorMessage, errorMessage, "warning"),
            )
          : openNotificationWithIcon(error.data.errorMessage, "", "warning");
      }
    }
  }, [transactionsWithError.isError, transactionsWithError.error]);

  const columns = [
    {
      title: "Merchant ID",
      dataIndex: "merchant",
      key: "merchant",
      width: "150px",
      editable: true,
    },

    {
      title: "CR-DR",
      dataIndex: "crDr",
      key: "crDr",
      width: "80px",
      align: "center",
      render: (value: TransactionType) => <Role role={value} />,
    },

    {
      title: "Account Name",
      dataIndex: "accountName",
      key: "accountName",
      width: "200px",
      editable: true,

      render: (_: unknown, record: TransactionDTO) => (
        <Typography.Text
          style={{
            width: 200,
            fontSize: "14px",
          }}
          ellipsis={{
            tooltip: {
              color: "#f4f4f4",
              title: <Typography.Text>{record.accountName}</Typography.Text>,
            },
          }}
        >
          {record.accountName}
        </Typography.Text>
      ),
    },

    {
      title: "Account Number",
      dataIndex: "accountNumber",
      key: "accountNumber",
      width: "110px",
      editable: true,
    },

    {
      title: "Sort Code",
      dataIndex: "sortCode",
      key: "sortCode",
      width: "100px",
      editable: true,
    },

    {
      title: "Payor",
      dataIndex: "payor",
      key: "payor",
      width: "150px",
      editable: true,
    },

    {
      title: "Narration",
      dataIndex: "narration",
      key: "narration",
      width: "150px",
      editable: true,
    },

    {
      title: "Error Message",
      dataIndex: "errorMessage",
      key: "errorMessage",
      width: "120px",
      fixed: "right",

      render: (_: unknown, record: TransactionDTO) => (
        <Typography.Text
          style={{
            width: 130,
            fontSize: "14px",
            textTransform: "lowercase",
          }}
          ellipsis={{
            tooltip: {
              color: "#f4f4f4",
              title: (
                <Space direction="vertical">
                  {JSON.parse(record?.errorMessage).map((message: string) => (
                    <Typography.Text style={{ fontSize: 13, display: "flex", gap: 5 }}>
                      <span> - </span> <span>{message}</span>
                    </Typography.Text>
                  ))}
                </Space>
              ),
            },
          }}
        >
          {JSON.parse(record?.errorMessage)[0]}
        </Typography.Text>
      ),
    },

    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: "100px",
      align: "center",
      fixed: "right",
      render: (_: any, record: TransactionDTO) => {
        const editable = isEditing(record);

        return editable ? (
          <span>
            <Typography.Link
              style={{ marginRight: 8, fontSize: 14 }}
              onClick={() => save(record.id)}
            >
              Save
            </Typography.Link>

            <Typography.Link style={{ fontSize: 14 }} onClick={cancel}>
              Cancel
            </Typography.Link>
          </span>
        ) : (
          <Typography.Link
            style={{ fontSize: 14 }}
            disabled={editingID !== -1 || updateSingleTransactionResult.isLoading}
            onClick={() => edit(record)}
          >
            {(editingID !== -1 || updateSingleTransactionResult.isLoading) &&
            form.getFieldValue("id") === record.id ? (
              <Spin size="small" />
            ) : (
              "Edit"
            )}
          </Typography.Link>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: TransactionDTO) => ({
        record,
        title: col.title,
        dataIndex: col.dataIndex,
        editing: isEditing(record),
        inputType: col.dataIndex === "Account Number" ? "number" : "text",
      }),
    };
  });

  return (
    <GenerationTableContainer>
      <Helmet>
        <title>NEFT Settlement | Edit-Transaction</title>
        <meta name="description" content="Helmet application" />
      </Helmet>
      <Row justify="space-between" style={{ padding: "7.5px 0px" }}></Row>

      <Form form={form} component={false}>
        <ConfigProvider
          renderEmpty={() =>
            transactionsWithError?.isError ? (
              <ApiErrorTableComponent queryResult={transactionsWithError} />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )
          }
        >
          <Table
            key="uuid"
            scroll={{ x: 1500 }}
            rowClassName="editable-row"
            rowKey={(record: TransactionDTO) => record?.id || ""}
            columns={mergedColumns as ColumnsType<TransactionDTO>}
            dataSource={transactionsWithError?.isError ? [] : formData}
            components={{
              body: {
                cell: EditableCell,
              },
            }}
            pagination={{
              current:
                transactionsWithError.data?.response?.number === 0
                  ? 1
                  : transactionsWithError.data?.response?.number &&
                    transactionsWithError.data?.response?.number + 1,
              showTitle: true,
              showSizeChanger: true,
              showQuickJumper: true,
              showPrevNextJumpers: true,
              pageSizeOptions: [5, 10, 20, 50, 100, 200],
              pageSize: transactionsWithError.data?.response.size,
              total: transactionsWithError.data?.response.totalElements,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
            loading={transactionsWithError.isFetching}
            onChange={handleTableChange}
          />
        </ConfigProvider>
      </Form>
    </GenerationTableContainer>
  );
};

export default GenerationEditableTable;
